import { formatOnboardingFieldLabel } from "../../features/auth/onboarding";
import { getTypedDealErrorMessage } from "../../features/deals/errorMessages";

export const LLM_ERROR_CODES = new Set([
  "LLM_RATE_LIMITED",
  "LLM_QUOTA_EXHAUSTED",
  "LLM_TIMEOUT",
  "LLM_UNAVAILABLE",
  "LLM_CONFIG_ERROR",
  "LLM_ERROR",
]);

export interface OnboardingFieldError {
  key: string;
  label: string;
  message: string;
}

export interface MissingFieldLabel {
  key: string;
  label: string;
}

export interface ParsedApiError {
  code?: string;
  message: string;
  suggestions?: string[];
  missingFields: string[];
  /** Same missing fields as `missingFields`, with the server's own label per
   * key (from `detail.missing_fields_detailed`) — prefer this over
   * `formatOnboardingFieldLabel` for display; it's always present for the
   * current onboarding config, unlike the frontend's static fallback map. */
  missingFieldsDetailed?: MissingFieldLabel[];
  /** Per-field errors for an onboarding step save (422 with `detail.fields`), keyed for inline focus. */
  fieldErrors?: OnboardingFieldError[];
  /** The onboarding step key the error applies to, when present. */
  stepKey?: string;
  isSellerOnboardingIncomplete: boolean;
  isDailyQuotaExceeded: boolean;
  isSellerNotVerified: boolean;
  isBuyerNotVerified: boolean;
  isLlmError: boolean;
}

/** Normalize legacy ``detail.error`` (e.g. daily_quota_exceeded) to ``code``. */
export function normalizeApiErrorCode(detail: Record<string, unknown>): string | undefined {
  if (typeof detail.code === "string" && detail.code.trim()) {
    return detail.code;
  }
  const legacy = detail.error;
  if (legacy === "daily_quota_exceeded") {
    return "DAILY_QUOTA_EXCEEDED";
  }
  if (typeof legacy === "string" && legacy.trim()) {
    return legacy.toUpperCase().replace(/-/g, "_");
  }
  return undefined;
}

/** Parse a FastAPI ``detail`` object (string or dict). */
export function parseApiDetailObject(
  detail: unknown,
  fallbackMessage = "Something went wrong.",
): ParsedApiError {
  if (Array.isArray(detail)) {
    const messages = detail.map((item: { msg?: string; field?: string | null }) => {
      const msg = item?.msg ?? "Validation error";
      const field = item?.field;
      return field ? `${field}: ${msg}` : msg;
    });
    return {
      message: messages.join(" · "),
      missingFields: [],
      isSellerOnboardingIncomplete: false,
      isDailyQuotaExceeded: false,
      isSellerNotVerified: false,
      isBuyerNotVerified: false,
      isLlmError: false,
    };
  }

  if (detail && typeof detail === "object") {
    const d = detail as Record<string, unknown>;
    const code = normalizeApiErrorCode(d);
    const missingFields = Array.isArray(d.missing_fields)
      ? d.missing_fields.filter((field): field is string => typeof field === "string")
      : [];
    const missingFieldsDetailed = Array.isArray(d.missing_fields_detailed)
      ? d.missing_fields_detailed
          .filter((f): f is Record<string, unknown> => !!f && typeof f === "object")
          .map((f) => ({ key: String(f.key ?? ""), label: String(f.label ?? f.key ?? "") }))
          .filter((f) => f.key)
      : undefined;
    const suggestions = Array.isArray(d.suggestions)
      ? d.suggestions.filter((s): s is string => typeof s === "string")
      : undefined;
    const isSellerOnboardingIncomplete = code === "SELLER_ONBOARDING_INCOMPLETE";
    const isDailyQuotaExceeded = code === "DAILY_QUOTA_EXCEEDED";
    const isLlmError = code ? LLM_ERROR_CODES.has(code) : false;
    const baseMessage =
      typeof d.message === "string" && d.message.trim().length > 0
        ? d.message
        : fallbackMessage;
    const fieldErrors = Array.isArray(d.fields)
      ? d.fields
          .filter((f): f is Record<string, unknown> => !!f && typeof f === "object")
          .map((f) => ({
            key: String(f.key ?? ""),
            label: String(f.label ?? f.key ?? ""),
            message: String(f.message ?? `${f.label ?? f.key} is required.`),
          }))
          .filter((f) => f.key)
      : undefined;
    const stepKey = typeof d.step_key === "string" ? d.step_key : undefined;

    const missingLabels = missingFieldsDetailed?.length
      ? missingFieldsDetailed.map((f) => f.label)
      : missingFields.map(formatOnboardingFieldLabel);

    return {
      code,
      message: isSellerOnboardingIncomplete && missingLabels.length > 0
        ? `${baseMessage} Missing: ${missingLabels.join(", ")}.`
        : baseMessage,
      suggestions,
      missingFields,
      missingFieldsDetailed,
      fieldErrors,
      stepKey,
      isSellerOnboardingIncomplete,
      isDailyQuotaExceeded,
      isSellerNotVerified: code === "SELLER_NOT_VERIFIED",
      isBuyerNotVerified: code === "BUYER_NOT_VERIFIED",
      isLlmError,
    };
  }

  if (typeof detail === "string" && detail.trim().length > 0) {
    return {
      message: detail,
      missingFields: [],
      isSellerOnboardingIncomplete: false,
      isDailyQuotaExceeded: false,
      isSellerNotVerified: false,
      isBuyerNotVerified: false,
      isLlmError: false,
    };
  }

  return {
    message: fallbackMessage,
    missingFields: [],
    isSellerOnboardingIncomplete: false,
    isDailyQuotaExceeded: false,
    isSellerNotVerified: false,
    isBuyerNotVerified: false,
    isLlmError: false,
  };
}

export function parseApiError(
  error: any,
  fallbackMessage = "Something went wrong.",
): ParsedApiError {
  const detail = error?.response?.data?.detail;

  if (Array.isArray(detail)) {
    return parseApiDetailObject(detail, fallbackMessage);
  }

  // Typed-deals envelope: { detail: { error: { code, message, field, received } } }.
  // Surface the seller-friendly copy first so the typed-deals validator output
  // is intelligible at the UI without each page re-implementing the lookup.
  const typedDealMsg = getTypedDealErrorMessage(error);
  if (typedDealMsg) {
    const typedCode =
      detail && typeof detail === "object" && !Array.isArray(detail) &&
      typeof (detail as { error?: { code?: string } }).error?.code === "string"
        ? (detail as { error: { code: string } }).error.code
        : undefined;
    return {
      code: typedCode,
      message: typedDealMsg,
      missingFields: [],
      isSellerOnboardingIncomplete: false,
      isDailyQuotaExceeded: false,
      isSellerNotVerified: false,
      isBuyerNotVerified: false,
      isLlmError: false,
    };
  }

  if (detail && typeof detail === "object") {
    return parseApiDetailObject(detail, fallbackMessage);
  }

  if (typeof detail === "string" && detail.trim().length > 0) {
    return parseApiDetailObject(detail, fallbackMessage);
  }

  if (typeof error?.message === "string" && error.message.trim().length > 0) {
    return {
      message: error.message,
      missingFields: [],
      isSellerOnboardingIncomplete: false,
      isDailyQuotaExceeded: false,
      isSellerNotVerified: false,
      isBuyerNotVerified: false,
      isLlmError: false,
    };
  }

  return parseApiDetailObject(undefined, fallbackMessage);
}
